/* globals showModalGeneric */
import React, { useState } from "react";
import Modal from "common/Modal";
import { atBqnTarget, safeStr, requestOnlineBqnIds, invalid } from "common/api";

export function toBool(val) {
  var num = Number(val);
  if (isNaN(num)) {
    return val == "yes" ? true : false;
  } else {
    return val;
  }
}

export function fromBool(val) {
  var num = Number(val);
  if (typeof val === "boolean") {
    return val ? "yes" : "no";
  } else if (val == undefined) {
    return "no";
  } else {
    return val;
  }
}

export function toBlockOrNumber(val) {
  var num = Number(val);
  if (isNaN(num)) {
    return "block";
  } else {
    return (Number(val) / 1000).toFixed(3);
  }
}

export function checkPercentage(attr) {
  return attr.endsWith("%");
}

export const parsePolicies = (response) => {
  const [head, ...rows] = response.trim("\n").split("\n");
  const headAttrs = head.trim().split(/\s+/);
  const nameIndex = headAttrs.indexOf('NAME');
  return rows.reduce((acc, row) => {
    const attrs = row.trim().split(/\s+/);
    const policyName = attrs[nameIndex];
    const policyValues = attrs.reduce((accValues, attr, index) => {
      accValues[headAttrs[index].toLowerCase()] = attr;
      return accValues;
    }, {});
    acc[policyName] = policyValues;
    return acc;
  }, {});
};

export const getPolicies = (type, mode, bqnId) => {
  const command = `show policy ${type}${type === "rate" ? "" : " web"}`;
  return ifCl.run(atBqnTarget(command, mode, bqnId)).then(parsePolicies);
};

export const BlockModal = ({ onClose, onApply }) => {
  const [open, setOpen] = useState(true);

  if (!open) {
    return null;
  }

  const handleApply = () => {
    onClose();
    onApply();
  };

  return (
    <Modal
      title="Are you sure ?"
      superIcon="warning"
      onClose={onClose}
      closeLabel="CANCEL"
      content={() => <>Traffic will be blocked when firing this policy</>}
      onApply={handleApply}
      applyLabel="OK"
    />
  );
};

export const parsePolicy = (response, responsePolicyMap) => {
  const lines = response.trim("\n").split("\n");
  const { policy } = lines.reduce(
    (acc, line) => {
      const [key, value] = line.split(":");
      if (/^\s/.test(key)) {
        const trimmedKey = key.trim();
        const policyPropMapped =
          responsePolicyMap[`${acc.base}: ${trimmedKey}:`];
        if (policyPropMapped) {
          const policyPropName = policyPropMapped.name;
          const trimmedValue = value.trim();
          const policyPropValue = policyPropMapped.valueF(trimmedValue);
          acc.policy[policyPropName] = policyPropValue;
        }
      } else {
        acc.base = key;
        const policyPropMapped = responsePolicyMap[`${key}:`];
        if (policyPropMapped) {
          const policyPropName = policyPropMapped.name;
          const trimmedValue = value.trim();
          const policyPropValue = policyPropMapped.valueF(trimmedValue);
          acc.policy[policyPropName] = policyPropValue;
        }
      }
      return acc;
    },
    { base: "", policy: {} }
  );
  return policy;
};

// The policy of the BQN selected in the header. A caller that resolved the
// BQN already, with bqnIdTarget, gives no mode: none is taken into account
// then, and the BQN given is asked for on its own.
export const getPolicy = (policy, mode, bqnId) =>
  ifCl.run(atBqnTarget(`show policy ${safeStr(policy)}`, mode, bqnId));

export function setBrateValue(value) {
  if (value === SSHZERO_DFLT_VAL) {
    return "default";
  } else {
    return Math.floor(Number(value) * 1000).toString();
  }
}

export function setBdurValue(value) {
  if (value === SSHZERO_DFLT_VAL) {
    return "default";
  } else {
    return value;
  }
}

export function setBthrValue(value) {
  if (value === SSHZERO_DFLT_VAL) {
    return "default";
  } else {
    return Math.floor(Number(value) * 1000).toString();
  }
}

export function setBthrwValue(value) {
  if (value === SSHBTHRW_DFLT_VAL) {
    return "default";
  } else {
    return value;
  }
}

export const SSHRATE_DFLT_VAL = 100000;
export const SSHZERO_DFLT_VAL = 0;
export const SSHBTHRW_DFLT_VAL = 300;

const _localeSettings = [
  "en-US",
  {
    minimumFractionDigits: 3,
    maximumFractionDigits: 3,
  },
];

function expressAsDataSpeed(value) {
  return value > 999999
    ? `${Number(value / 1000000).toLocaleString(..._localeSettings)} Gbps`
    : value > 999
    ? `${Number(value / 1000).toLocaleString(..._localeSettings)} Mbps`
    : `${Number(value).toLocaleString(..._localeSettings)} Kbps`;
}

function inDataSpeedUnits(value) {
  return value === "no" || value === false
    ? "no"
    : expressAsDataSpeed(parseFloat(value));
}

function splitFieldAndValue(line) {
  return line.split(/\:\s/);
}

function wrapIntoIndentedSpan(text) {
  return `<span style="margin-left: 2ch;">${keepIndentation(text)}</span>`;
}

const NOT_ALLOWED_DYN_POLICY = ["Type", "DOS", "Dynamic update"];

const TIME_FIELDS_DYN_POLICY = [
  "Burst duration",
  "Burst threshold window",
  "Burst threshold duration",
  "Burst transition duration",
];

const SPEED_FIELDS_DYN_POLICY = [
  "Rate",
  "Burst rate",
  "Burst threshold",
  "Burst threshold",
];

function arrangeUnits([field, value]) {
  return TIME_FIELDS_DYN_POLICY.includes(field.trim(" "))
    ? [field, value + " s"]
    : SPEED_FIELDS_DYN_POLICY.includes(field.trim(" "))
    ? [field, inDataSpeedUnits(value)]
    : [field, value];
}

function onlyAllowed([field, _value]) {
  return NOT_ALLOWED_DYN_POLICY.includes(field) !== true;
}

function joinRecord([field, value]) {
  return `${field}: ${value}`;
}

function keepIndentation(fact) {
  return fact.startsWith("  ")
    ? wrapIntoIndentedSpan(fact.replace(/^\s\s/, ""))
    : fact;
}

export function parseClusterResponse(response) {
  const bqnResponses = response.trim().split("\n\n");
  const parsed = {};
  for (const resp of bqnResponses) {
    const lines = resp.split("\n");
    const bqnIdLine = lines.find((line) => line.startsWith("BQN ID:"));
    if (bqnIdLine) {
      const [, bqnId] = bqnIdLine.split(":");
      if (bqnId) {
        parsed[bqnId.trim()] = resp;
      }
    }
  }
  return parsed;
}

const textsToHtml = source =>
  source.split('\n\n').map(textToHtml);

const textToHtml = source =>
  `<p style="line-height: 20px !important; margin-left: 12px">${
    source.trim().split('\n')
      .map(splitFieldAndValue)
      .filter(onlyAllowed)
      .map(arrangeUnits)
      .map(joinRecord)
      .map(keepIndentation)
      .join("<br>")
  }</p>`;

function parsePolicyDetails(policyText, idLine, reducedOutput = false) {
  let lines = policyText.trim().split("\n");
  lines = lines.filter(
    (line) =>
      !line.startsWith("Source:") &&
    (!reducedOutput ||
      line.search(/Burst|Blocking|Auto-Congestion-Management/) === -1)
  );
  if (idLine) {
    lines.splice(1, 0, idLine);
  }
  return lines.join('\n')
}

const areSameNodeIds = (expected, obtained) =>
  ( (one, another) => one.size === another.size && expected.every(id => obtained.includes(id)))(
    new Set(expected), new Set(obtained)
  );

const replaceBqnIdWithAll = input =>
  input.replace(/BQN ID: (\d+)/, 'BQN ID: All')

export const tryToMergeResults = (results) =>
  (Object.keys(results).length > 1
      ? requestOnlineBqnIds()
      : Promise.reject('must be more than 1 node'))
  .then( bqnIds =>
    areSameNodeIds(bqnIds, Object.keys(results)) !== true ? invalid('nodes not matching') :
    Object.values(results)
      .map(replaceBqnIdWithAll)
      .reduce( (result, current) =>
        result === null || result === current ? current :
        invalid('results not matching'), null)
  ).catch( () => Object.values(results).join("\n\n"))

const showPolicyModal = result =>
    showModalGeneric("hide", "Policy Details", textsToHtml(result), true);

// The answer of a command sent with the "bqn-id ID" option reports no BQN ID
// of its own: it carries the policy of the BQN asked for alone, and it is
// taken as the one section of that BQN, with the header the sections of a
// cluster answer have, so that the details read the same either way.
export const policiesByBqnGiven = (response, bqnId) => {
  const policiesByBqn = parseClusterResponse(response);

  return Object.keys(policiesByBqn).length > 0 || response.trim() === ""
    ? policiesByBqn
    : { [bqnId]: `BQN ID: ${bqnId}\n${response.trim()}` };
};

// The policy of the BQN selected in the header, which is the whole cluster
// when the selection is "all": every BQN reports its own section then, and
// the sections that agree are merged into one.
export const showDynPolicyRateCluster = (policyName, mode, bqnId) =>
  getPolicy(policyName, mode, bqnId)
    .then( response => {
      const policiesByBqn = policiesByBqnGiven(response, bqnId);
      return Object.entries(policiesByBqn).reduce(
        (acc, [currentBqnId, policyText]) => {
          const policyIdLine = null;
          acc[currentBqnId] = parsePolicyDetails(policyText, policyIdLine);
          return acc;
        },
        {});
    })
    .then( infoBybqnId =>
      bqnId === 'all' ? tryToMergeResults(infoBybqnId) :
      infoBybqnId[bqnId] ?? invalid(`No details of policy "${policyName}"`)
    )
    .then(showPolicyModal)

// The policy of the BQN given, and of no BQN in particular when it is none:
// the caller resolved the BQN already, so there is no mode to take into
// account here.
export const showDynPolicyRate = (policyName, reducedOutput, bqnId) =>
  getPolicy(policyName, undefined, bqnId)
    .then(response => parsePolicyDetails(response, null, reducedOutput))
    .then(showPolicyModal)

